import { AuthClient } from "@supabase/auth-js";
import type { PublicConfig } from "./config";

// The browser talks to Supabase only for Auth (photos will be uploaded straight to Storage
// with signed URLs from the API). All data goes through the Go API, so only the small
// auth client is bundled, not the whole supabase-js.
type Client = { auth: InstanceType<typeof AuthClient> };
let client: Client | null = null;
let domain = "users.gymlog.local";

// "Remember me": the session lives in localStorage (kept until logout) or, when unchecked, in
// sessionStorage (gone when the tab or installed app is closed). The choice itself is kept in
// localStorage; no choice yet means remember, as before.
const REMEMBER_KEY = "iron-remember";

function remembered(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Call before signing in: where the new session is stored. */
export function setRemember(on: boolean) {
  try {
    localStorage.setItem(REMEMBER_KEY, on ? "1" : "0");
  } catch {
    // Storage blocked: the session behaves as remembered.
  }
}

const sessionStore = {
  getItem(key: string) {
    try {
      return (remembered() ? localStorage : sessionStorage).getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string) {
    try {
      const [keep, drop] = remembered() ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
      keep.setItem(key, value);
      drop.removeItem(key);
    } catch {
      // Storage blocked: the session lasts only while the page is open.
    }
  },
  removeItem(key: string) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // Nothing stored.
    }
  },
};

export function initSupabase(cfg: PublicConfig) {
  client ??= {
    auth: new AuthClient({
      url: `${cfg.supabaseUrl}/auth/v1`,
      headers: { apikey: cfg.supabaseAnonKey, Authorization: `Bearer ${cfg.supabaseAnonKey}` },
      // Same storage key as supabase-js, so a session survives switching between the two.
      storageKey: `sb-${new URL(cfg.supabaseUrl).hostname.split(".")[0]}-auth-token`,
      persistSession: true,
      storage: sessionStore,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    }),
  };
  domain = cfg.authEmailDomain;
  return client;
}

export function supabase(): Client {
  if (!client) throw new Error("Supabase used before initSupabase()");
  return client;
}

/** Usernames are stored as an internal email address in Supabase Auth. */
export function loginEmail(username: string): string {
  const u = username.trim().toLowerCase();
  return u.includes("@") ? u : `${u}@${domain}`;
}

/** Signs out; the auth listener in main.tsx clears cached data and goes to /login. */
export async function logout() {
  await supabase().auth.signOut();
}
